/**
 * Pure helpers for the Appointment module (no React, no network) so they can be unit-tested.
 * The server owns every rule (status machine, slot capacity, duplicates); these only decide what the UI offers.
 */

export const APPOINTMENT_STATUSES = [
  { code: 'BOOKED', label: 'Booked', tone: 'info' },
  { code: 'CONFIRMED', label: 'Confirmed', tone: 'success' },
  { code: 'REGISTERED', label: 'Registered', tone: 'done' },
  { code: 'CANCELLED', label: 'Cancelled', tone: 'danger' },
  { code: 'NO_SHOW', label: 'No-show', tone: 'warning' },
];

export const statusMeta = (code) =>
  APPOINTMENT_STATUSES.find((s) => s.code === code) || { code, label: code || 'Unknown', tone: 'info' };

const isActive = (a) => !!a && (a.status === 'BOOKED' || a.status === 'CONFIRMED');

export const canConfirm = (a) => !!a && a.status === 'BOOKED';
export const canReschedule = (a) => isActive(a);
export const canCancel = (a) => isActive(a);
export const canNoShow = (a) => isActive(a);
/** The patient can be marked as arrived (token given) while the appointment is open and nobody has arrived for it yet. */
export const canArrive = (a) => isActive(a) && !a.arrivedAt;
/** Only a CONFIRMED appointment may be handed to Patient Registration. */
export const canRegister = (a) => !!a && a.status === 'CONFIRMED';

const toMinutes = (hhmm) => {
  const [h, m] = String(hhmm || '0:0').split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};

/**
 * Where an open appointment stands against the clock, so the desk can see who is due and who did not come.
 *   upcoming: more than 60 minutes away      soon: starts within 60 minutes
 *   now: the slot is running                 overdue: the slot has ended and nobody was registered
 * Final statuses (REGISTERED, CANCELLED, NO_SHOW) have no timing.
 */
export const appointmentTiming = (a, now = new Date()) => {
  if (!isActive(a)) return null;
  const [y, m, d] = dateOnly(a.appointmentDate).split('-').map(Number);
  const day = new Date(y, m - 1, d);
  const start = new Date(day.getTime() + toMinutes(a.slotStart) * 60000);
  const end = new Date(day.getTime() + toMinutes(a.slotEnd) * 60000);
  const minsToStart = Math.round((start - now) / 60000);
  const human = (mins) => {
    const abs = Math.abs(mins);
    if (abs >= 1440) return `${Math.floor(abs / 1440)}d`;
    return abs >= 60 ? `${Math.floor(abs / 60)}h ${abs % 60}m`.replace(' 0m', '') : `${abs} min`;
  };
  if (now >= end) return { state: 'overdue', label: `Slot ended ${human(Math.round((now - end) / 60000))} ago` };
  if (now >= start) return { state: 'now', label: 'Slot running now' };
  if (minsToStart <= 60) return { state: 'soon', label: `Starts in ${human(minsToStart)}` };
  return { state: 'upcoming', label: minsToStart < 1440 ? `Starts in ${human(minsToStart)}` : '' };
};

/**
 * Quick pick for "this patient is coming in about an hour". Picks the first bookable slot of today that starts at least
 * `offsetMinutes` from now (0 = the next one available). Slots are the server's list, so full or past ones are never chosen.
 */
export const pickQuickSlot = (slots, now = new Date(), offsetMinutes = 0) => {
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const hit = (slots || []).find((s) => s.isBookable && toMinutes(s.slotStart) >= nowMin + offsetMinutes);
  return hit ? hit.slotStart : null;
};

// ---- dates (plain yyyy-MM-dd strings, no time zones involved)

export const toIsoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const todayIso = () => toIsoDate(new Date());

export const addDaysIso = (iso, days) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toIsoDate(dt);
};

/** "2026-10-20T00:00:00" or "2026-10-20" -> "2026-10-20" */
export const dateOnly = (value) => (value ? String(value).split('T')[0] : '');

export const formatDateLabel = (value) => {
  const iso = dateOnly(value);
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
};

export const slotLabel = (a) => (a ? `${a.slotStart} - ${a.slotEnd}` : '');

// ---- identity

export const ageLabel = (a) => {
  if (!a) return '';
  if (a.ageYear !== null && a.ageYear !== undefined) return `${a.ageYear}Y`;
  if (a.dob) return dateOnly(a.dob);
  return '';
};

export const newIdempotencyKey = () =>
  (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `apt-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/**
 * What the booking form may send. An existing patient is sent by UHID only (the server reads identity from the
 * patient master); a new patient carries the minimum registration will also require.
 */
/** True for the "Home Collection" visit type (the server applies the same rule). */
export const isHomeVisit = (visitType) => /home/i.test(visitType || '');

export const buildCreatePayload = (form, idempotencyKey) => {
  const tests = form.tests || [];
  const home = isHomeVisit(form.visitType);
  const base = {
    centreId: form.centreId ? Number(form.centreId) : 0,
    departmentId: form.departmentId ? Number(form.departmentId) : undefined,
    visitType: form.visitType || undefined,
    doctorId: form.doctorId ? Number(form.doctorId) : undefined,
    scheduledDoctorId: form.scheduledDoctorId ? Number(form.scheduledDoctorId) : undefined,
    purpose: form.purpose?.trim() || undefined,
    remarks: form.remarks?.trim() || undefined,
    appointmentDate: form.appointmentDate,
    slotStart: form.slotStart,
    idempotencyKey,
    // Only the ids (and names, for error messages) go to the server: it reads the real data and price from its masters.
    tests: tests.length > 0 ? tests.map((t) => ({ itemId: t.itemId, name: t.name })) : undefined,
    collectionAddress: home ? form.collectionAddress?.trim() || undefined : undefined,
    collectionPincode: home ? form.collectionPincode?.trim() || undefined : undefined,
    phlebotomistName: home ? form.phlebotomistName?.trim() || undefined : undefined,
    rebookedFromId: form.rebookedFromId || undefined,
  };
  if (form.patientId) return { ...base, patientId: form.patientId };
  const age = form.ageYear !== '' && form.ageYear !== null && form.ageYear !== undefined ? parseInt(form.ageYear, 10) : undefined;
  return {
    ...base,
    title: form.title || undefined,
    pName: form.pName?.trim(),
    gender: form.gender,
    dob: form.dob || undefined,
    ageYear: Number.isNaN(age) ? undefined : age,
    mobile: form.mobile?.trim(),
    email: form.email?.trim() || undefined,
  };
};

/** First problem the form has, or null. Mirrors the server so the user is told before the round trip; the server stays the authority. */
export const validateBooking = (form) => {
  if (!form.patientId) {
    if (!form.pName?.trim()) return 'Patient name is required.';
    if (!form.gender) return 'Gender is required.';
    if (!form.mobile?.trim()) return 'Mobile number is required.';
    if (!form.dob && (form.ageYear === '' || form.ageYear === null || form.ageYear === undefined)) return 'Enter date of birth or age.';
    const age = parseInt(form.ageYear, 10);
    if (form.ageYear !== '' && (Number.isNaN(age) || age < 0 || age > 120)) return 'Please enter a valid age (0 to 120 years).';
  }
  if (!form.appointmentDate) return 'Choose the appointment date.';
  if (!form.slotStart) return 'Choose a time slot.';
  if (isHomeVisit(form.visitType) && !form.collectionAddress?.trim()) return 'Enter the collection address for a home collection.';
  if (form.repeat) {
    const n = parseInt(form.occurrences, 10);
    if (Number.isNaN(n) || n < 2 || n > 12) return 'A repeating booking needs between 2 and 12 appointments.';
  }
  return null;
};

/**
 * Registration hand-off. For a known patient only the UHID matters (the registration screen loads the patient through
 * its normal path). For a new patient the captured demographics pre-fill the form.
 * Keys match RegistrationPage's patientData state.
 */
export const buildRegistrationPrefill = (a) => {
  if (!a) return null;
  return {
    appointmentId: a.appointmentId,
    appointmentNo: a.appointmentNo,
    patientId: a.patientId || null,
    centreId: a.centreId,
    visitType: a.visitType || 'Center Visit',
    doctorId: a.doctorId || null,
    doctorName: a.doctorName || '',
    patientData: {
      title: a.title || 'Mr.',
      pName: a.pName || '',
      gender: a.gender || 'Male',
      dob: dateOnly(a.dob),
      isDobActual: !!a.dob,
      ageYear: a.ageYear !== null && a.ageYear !== undefined ? String(a.ageYear) : '',
      ageMonth: a.ageMonth !== null && a.ageMonth !== undefined ? String(a.ageMonth) : '',
      ageDays: a.ageDays !== null && a.ageDays !== undefined ? String(a.ageDays) : '',
      mobile: a.mobile || '',
      email: a.email || '',
    },
  };
};

/**
 * Booking form values for "Rebook" of a cancelled or missed appointment: everything except the date and slot, which the
 * desk chooses again. The link to the original is sent as rebookedFromId.
 */
export const buildRebookPrefill = (a) => {
  if (!a) return null;
  return {
    form: {
      patientId: a.patientId || '',
      patientLabel: a.patientId ? `${a.pName} · ${a.patientId} · ${a.mobile}` : '',
      title: a.title || 'Mr.',
      pName: a.pName || '',
      gender: a.gender || '',
      dob: dateOnly(a.dob),
      ageYear: a.ageYear !== null && a.ageYear !== undefined ? String(a.ageYear) : '',
      mobile: a.mobile || '',
      email: a.email || '',
      centreId: a.centreId,
      departmentId: a.departmentId ? String(a.departmentId) : '',
      visitType: a.visitType || 'Center Visit',
      doctorId: a.doctorId ? String(a.doctorId) : '',
      purpose: a.purpose || '',
      remarks: '',
      tests: a.tests || [],
      collectionAddress: a.collectionAddress || '',
      collectionPincode: a.collectionPincode || '',
      phlebotomistName: a.phlebotomistName || '',
      rebookedFromId: a.appointmentId,
    },
    patientInfo: a.patientId
      ? { patientId: a.patientId, pName: a.pName, fullName: `${a.title || ''} ${a.pName}`.trim(), gender: a.gender, ageYear: a.ageYear, mobile: a.mobile }
      : null,
  };
};

/** A cancelled or missed appointment can be booked again; a registered one cannot (the patient was already seen). */
export const canRebook = (a) => !!a && (a.status === 'CANCELLED' || a.status === 'NO_SHOW');

export const registrationPathFor = (appointmentId) => `/registration?appointmentId=${encodeURIComponent(appointmentId)}`;

/** Reads ?appointmentId=N from a location.search string; returns a positive integer or null. */
export const parseAppointmentIdParam = (search) => {
  const raw = new URLSearchParams(search || '').get('appointmentId');
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isInteger(n) && n > 0 ? n : null;
};

export const WEEKDAYS = [
  { value: 0, label: 'Sun' }, { value: 1, label: 'Mon' }, { value: 2, label: 'Tue' }, { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' }, { value: 5, label: 'Fri' }, { value: 6, label: 'Sat' },
];

export const parseClosedWeekdays = (value) =>
  new Set(String(value || '').split(',').map((s) => s.trim()).filter((s) => s !== '').map(Number).filter((n) => n >= 0 && n <= 6));

export const serializeClosedWeekdays = (set) => [...set].sort((a, b) => a - b).join(',');
